import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { tokens } from "../components/Theme";

import GetMarkets from "../api/market/GetAll_market";
import CreateMarket from "../api/market/Create_market";
import UpdateMarket from "../api/market/Update_market";
import DeleteMarketApi from "../api/market/Delete_market";

function Market() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const userData = JSON.parse(localStorage.getItem("userdata")) || {};
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const defaultRole = userData.level === 1 ? "admin" : "user";
  const [role] = useState(defaultRole);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const confirmDelete = (index) => {
    setDeleteIndex(index);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    handleDelete(deleteIndex);
    setConfirmOpen(false);
    setDeleteIndex(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      const res = await GetMarkets();
      if (res.status === 200) {
        setData(res.payload);
      }
    };
    fetchData();
  }, []);

  const handleChange = (index, key, value) => {
    const updated = [...data];
    updated[index][key] = value;
    setData(updated);
  };

  const handleAdd = () => {
    const timestamp = Math.floor(Date.now() / 1000);
    const appPriceId = `PK${timestamp}`;

    const newItem = {
      appPriceId,
      province: "",
      breedName: "",
      priceDate: "",
      avgPrice: 0,
      dataSource: "",
    };

    setData((prev) => [...prev, newItem]);
  };

  const handleDelete = async (index) => {
    try {
      const id = data[index].id;
      if (id) {
        const res = await DeleteMarketApi(id);
        if (res.status !== 200) {
          showSnackbar("ลบไม่สำเร็จ", "error");
          return;
        }
      }
      setData((prev) => prev.filter((_, i) => i !== index));
      showSnackbar("ลบข้อมูลเรียบร้อย", "success");
    } catch (err) {
      showSnackbar("เกิดข้อผิดพลาดขณะลบ", "error");
    }
  };

  const handleSave = async () => {
    try {
      for (const item of data) {
        const payload = {
          appPriceId: item.appPriceId,
          province: item.province,
          breedName: item.breedName,
          priceDate: item.priceDate,
          avgPrice: Number(item.avgPrice),
          dataSource: item.dataSource,
        };

        if (item.id) {
          await UpdateMarket(item.id, payload);
        } else {
          await CreateMarket(payload);
        }
      }

      showSnackbar("บันทึกข้อมูลสำเร็จ", "success");

      const refreshed = await GetMarkets();
      if (refreshed.status === 200) {
        setData(refreshed.payload);
      }
    } catch {
      showSnackbar("เกิดข้อผิดพลาดระหว่างบันทึก", "error");
    }
  };

  const paginatedData = data.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Box p={2}>
      <Typography variant="h6" mb={2}>
        ราคาผลผลิตการเกษตร
      </Typography>

      {isMobile ? (
        paginatedData.map((row, index) => {
          const realIndex = (page - 1) * rowsPerPage + index;
          return (
            <Accordion key={realIndex}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{row.breedName || "ไม่มีชื่อพันธุ์"}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {[
                  "province",
                  "breedName",
                  "priceDate",
                  "avgPrice",
                  "dataSource",
                ].map((key) => (
                  <TextField
                    key={key}
                    label={key}
                    fullWidth
                    margin="dense"
                    type={
                      key === "priceDate"
                        ? "date"
                        : key === "avgPrice"
                          ? "number"
                          : "text"
                    }
                    size="small"
                    value={row[key]}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: role !== "admin" }}
                    onChange={(e) =>
                      handleChange(realIndex, key, e.target.value)
                    }
                  />
                ))}
                {role === "admin" && (
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    sx={{ mt: 1 }}
                    onClick={() => confirmDelete(realIndex)}
                  >
                    ลบรายการนี้
                  </Button>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })
      ) : (
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ลำดับ</TableCell>
                <TableCell>จังหวัด</TableCell>
                <TableCell>พันธุ์</TableCell>
                <TableCell>วันที่</TableCell>
                <TableCell>ราคาเฉลี่ย</TableCell>
                <TableCell>แหล่งข้อมูล</TableCell>
                {role === "admin" && <TableCell>ลบ</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => {
                const realIndex = (page - 1) * rowsPerPage + index;
                return (
                  <TableRow key={realIndex}>
                    <TableCell>{realIndex + 1}</TableCell>
                    {[
                      "province",
                      "breedName",
                      "priceDate",
                      "avgPrice",
                      "dataSource",
                    ].map((key) => (
                      <TableCell key={key}>
                        <TextField
                          variant="standard"
                          InputProps={{
                            disableUnderline: true,
                            readOnly: role !== "admin",
                            sx: {
                              minWidth: "120px",
                              input: {
                                color: "#000",
                                WebkitTextFillColor: "#000",
                              },
                            },
                          }}
                          size="small"
                          type={
                            key === "priceDate"
                              ? "date"
                              : key === "avgPrice"
                                ? "number"
                                : "text"
                          }
                          value={row[key]}
                          onChange={(e) =>
                            handleChange(realIndex, key, e.target.value)
                          }
                        />
                      </TableCell>
                    ))}
                    {role === "admin" && (
                      <TableCell>
                        <IconButton
                          color="error"
                          onClick={() => confirmDelete(realIndex)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Pagination
        sx={{ mt: "10px", marginX: isMobile ? 12 : 0 }}
        count={Math.ceil(data.length / rowsPerPage)}
        page={page}
        onChange={(e, value) => setPage(value)}
        color="primary"
      />

      {role === "admin" && (
        <Box mt={2} display="flex" justifyContent="space-between">
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            เพิ่มข้อมูล
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave}>
            บันทึก
          </Button>
        </Box>
      )}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>ยืนยันการลบ</DialogTitle>
        <DialogContent>
          <Typography>คุณต้องการลบรายการนี้หรือไม่?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>ยกเลิก</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            ลบ
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

export default Market;
